// src/App.tsx
import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import PublicLayout from './layouts/PublicLayout';
import PrivateLayout from './layouts/PrivateLayout';
import Cookies from 'js-cookie';

// Публичные страницы
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';

// Приватные страницы
import Accounts from './pages/Accounts';
import AddAccount from './pages/AddAccount';
import NeuroCopywriting from './pages/NeuroCopywriting';
import API from './pages/API';
import Parsing from './pages/Parsing';
import Files from './pages/Files';
import StopWords from './pages/StopWords';
import IncludeWords from './pages/IncludeWords';
import NeuroCommenting from './pages/NeuroCommenting';
import ProxyPage from './pages/Proxy';
import Profile from './pages/Profile';
import NeuroReactor from './pages/NeuroReactor';
import MassSending from './pages/MassSending';
import AccountDetail from './pages/AccountDetail';
import ReactorDetail from './pages/ReactorDetail';
import CopywriterDetail from './pages/CopywriterDetail';
import NeuroCommentingDetail from './pages/NeuroCommentingDetail';
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ForgotPassword from "./pages/ForgotPassword";
import MassSendingDetail from "./pages/MassSendingDetail";
import ChannelSubscription from "./pages/ChannelSubscription";
import ChannelSubscriptionDetail from "./pages/ChannelSubscriptionDetail";
import LeadGenerator from "./pages/LeadGenerator";
import LeadGeneratorDetail from "./pages/LeadGeneratorDetail";
import Inviter from "./pages/Inviter";
import InviterDetail from "./pages/InviterDetail";
import StoryMentions from "./pages/StoryMentions";
import StoryMentionsDetail from "./pages/StoryMentionsDetail";
import Warmup from "./pages/Warmup";
import WarmupDetail from './pages/WarmupDetail';
import Support from './pages/Support';
import ProxyChannels from "./pages/ProxyChannels";
import ProtectedRoute from './components/ProtectedRoute';
import Groups from './pages/Groups'
import BulkChangeInfo from "./pages/BulkChangeInfo";



// === Глобальный Cookie Consent Banner ===
const CookieConsentBanner: React.FC = () => {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const consent = Cookies.get('typhon_cookie_consent');
    if (!consent) {
      setOpen(true);
    }
  }, []);

  const handleAccept = () => {
    Cookies.set('typhon_cookie_consent', 'true', { expires: 365 });
    setOpen(false);
  };

  const handleDecline = () => {
    Cookies.set('typhon_cookie_consent', 'false', { expires: 365 });
    setOpen(false);
    // Опционально: можно очистить localStorage при отказе
    // localStorage.clear();
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
        color: 'white',
        padding: '16px 24px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.5)',
        borderRadius: '16px 16px 0 0',
        fontFamily: '"Inter", "Roboto", sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: window.innerWidth < 640 ? 'column' : 'row',
          alignItems: 'center',
          gap: '20px',
          textAlign: window.innerWidth < 640 ? 'center' : 'left',
        }}
      >
        <div style={{ flex: 1, fontSize: '0.95rem', lineHeight: '1.6' }}>
          Мы используем <strong>cookie-файлы</strong> и <strong>localStorage</strong> для авторизации, сохранения настроек и работы личного кабинета.
          Это необходимо для корректной работы Typhon.
          Продолжая использовать сайт, вы соглашаетесь с нашей{' '}
          <a href="/privacy" target="_blank" rel="noopener" style={{ color: '#60A5FA', textDecoration: 'underline' }}>
            политикой конфиденциальности
          </a>
          .
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={handleDecline}
            style={{
              padding: '10px 24px',
              background: 'transparent',
              border: '1px solid #475569',
              color: 'white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Отклонить
          </button>
          <button
            onClick={handleAccept}
            style={{
              padding: '10px 32px',
              background: '#60A5FA',
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              boxShadow: '0 4px 16px rgba(96, 165, 250, 0.4)',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#3B82F6'}
            onMouseOut={(e) => e.currentTarget.style.background = '#60A5FA'}
          >
            Принять и продолжить
          </button>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  // @ts-ignore
    return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* ПУБЛИЧНЫЕ */}

          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
          </Route>

            {/* ПРИВАТНЫЕ */}
            <Route element={<PrivateLayout/>}>
                <Route path="/accounts" element={<ProtectedRoute><Accounts/></ProtectedRoute>}/>
                <Route path="/accounts/add" element={<ProtectedRoute><AddAccount/></ProtectedRoute>}/>
                <Route path="/accounts/:id" element={<ProtectedRoute><AccountDetail/></ProtectedRoute>}/>
                <Route path="/exports" element={<ProtectedRoute><Files/></ProtectedRoute>}/>
                <Route path="/files" element={<ProtectedRoute><Files/></ProtectedRoute>}/>
                <Route path="/api" element={<ProtectedRoute><API/></ProtectedRoute>}/>
                <Route path="/parsing/:tab" element={<ProtectedRoute featureKey="parsing"><Parsing/></ProtectedRoute>}/>
                <Route path="/stopwords" element={<ProtectedRoute><StopWords/></ProtectedRoute>}/>
                <Route path="/includewords" element={<ProtectedRoute><IncludeWords/></ProtectedRoute>}/>
                <Route path="/proxy" element={<ProtectedRoute><ProxyPage/></ProtectedRoute>}/>
                <Route path="/neuro_commenting"
                       element={<ProtectedRoute featureKey="neurocommenting"><NeuroCommenting/></ProtectedRoute>}/>
                <Route path="/neuro_commenting/detail/:taskId" element={<ProtectedRoute
                    featureKey="neurocommenting"><NeuroCommentingDetail/></ProtectedRoute>}/>
                <Route path="/profile" element={<ProtectedRoute><Profile/></ProtectedRoute>}/>
                <Route path="/neurocopywriting"
                       element={<ProtectedRoute featureKey="neurocopywright"><NeuroCopywriting/></ProtectedRoute>}/>
                <Route path="/reactor"
                       element={<ProtectedRoute featureKey="reactors"><NeuroReactor/></ProtectedRoute>}/>
                <Route path="/mass_sending"
                       element={<ProtectedRoute featureKey="broadcast"><MassSending/></ProtectedRoute>}/>
                <Route path="/mass_sending/detail/:taskId"
                       element={<ProtectedRoute featureKey="broadcast"><MassSendingDetail/></ProtectedRoute>}/>
                <Route path="/reactor/detail/:jobId"
                       element={<ProtectedRoute featureKey="reactors"><ReactorDetail/></ProtectedRoute>}/>
                <Route path="/copywriting/detail/:taskId"
                       element={<ProtectedRoute featureKey="neurocopywright"><CopywriterDetail/></ProtectedRoute>}/>
                <Route path="/channel_subscription" element={<ProtectedRoute
                    featureKey="autosubscriptions"><ChannelSubscription/></ProtectedRoute>}/>
                <Route path="/subscribe_to_channels/detail/:jobId" element={<ProtectedRoute
                    featureKey="autosubscriptions"><ChannelSubscriptionDetail/></ProtectedRoute>}/>
                <Route path="/lead_generator"
                       element={<ProtectedRoute featureKey="leadgen"><LeadGenerator/></ProtectedRoute>}/>
                <Route path="/lead_generator/detail/:jobId"
                       element={<ProtectedRoute featureKey="leadgen"><LeadGeneratorDetail/></ProtectedRoute>}/>
                <Route path="/inviter" element={<ProtectedRoute featureKey="inviter"><Inviter/></ProtectedRoute>}/>
                <Route path="/inviter/detail/:jobId"
                       element={<ProtectedRoute featureKey="inviter"><InviterDetail/></ProtectedRoute>}/>
                <Route path="/story_mentions"
                       element={<ProtectedRoute featureKey="story_mentions"><StoryMentions/></ProtectedRoute>}/>
                <Route path="/story_mentions/detail/:jobId"
                       element={<ProtectedRoute featureKey="story_mentions"><StoryMentionsDetail/></ProtectedRoute>}/>
                <Route path="/warmup" element={<ProtectedRoute featureKey="warmup"><Warmup/></ProtectedRoute>}/>
                <Route path="/warmup/detail/:jobId"
                       element={<ProtectedRoute featureKey="warmup"><WarmupDetail/></ProtectedRoute>}/>
                <Route path="/support" element={<ProtectedRoute><Support/></ProtectedRoute>}/>
                <Route path="/proxy-channels" element={<ProtectedRoute><ProxyChannels/></ProtectedRoute>}/>
                <Route path ='/groups' element={<ProtectedRoute><Groups/></ProtectedRoute>}/>
                <Route path ='/bulk-change-info' element={<ProtectedRoute><BulkChangeInfo/></ProtectedRoute>}/>

            </Route>
        </Routes>

          <CookieConsentBanner/>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
